// Apolo IDE — RepoSlugMenu (Pedro 2026-06-25, sprint cursor-strip-3).
//
// Drop-up que abre quando o usuário clica no slug do repo (ex:
// pedromleal15/apolo-IDE) na GitConnectionBar. Lista repos abertos
// recentemente + ação "Abrir outra pasta...". Selecionar troca o workspace
// REAL via openFolderForSession (window.apolo.repo.pickFolder).
//
// Recents vivem em LS (`apolo:repo:recents`, array de paths, cap 8) — sem
// migration D1. A cada openFolder bem-sucedido, push pro topo + dedup.
//
// Presentacional + LS apenas — quem chama o IPC é openFolderForSession
// (já existente). Aqui só orquestra UX.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FolderOpen, Check, History, X, Github, Loader2, Search, Lock } from 'lucide-react';
import { openFolderForSession } from '../../lib/openFolder.js';
import { hasElectronBridge } from '../../lib/electronBridge.js';
import { apiFetch } from '../../lib/api.js';

const LS_RECENTS = 'apolo:repo:recents';
const MAX_RECENTS = 8;

export function readRepoRecents() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LS_RECENTS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p) => typeof p === 'string' && p) : [];
  } catch {
    return [];
  }
}

export function writeRepoRecents(paths) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_RECENTS, JSON.stringify(paths.slice(0, MAX_RECENTS)));
  } catch {
    /* best-effort */
  }
}

export function pushRepoRecent(path) {
  if (!path || typeof path !== 'string') return;
  const cur = readRepoRecents();
  const next = [path, ...cur.filter((p) => p !== path)].slice(0, MAX_RECENTS);
  writeRepoRecents(next);
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('apolo:repo:recents:change', { detail: { paths: next } }));
    } catch { /* ignore */ }
  }
}

export function shortenPath(p) {
  if (!p) return '';
  if (typeof window !== 'undefined' && window.apolo?.env?.home) {
    // Best-effort if app exposed $HOME; otherwise just basename.
    try {
      const home = window.apolo.env.home;
      if (typeof home === 'string' && home && p.startsWith(home)) {
        return '~' + p.slice(home.length);
      }
    } catch { /* ignore */ }
  }
  // Basename fallback: keep last 2 segments.
  const parts = p.split('/').filter(Boolean);
  if (parts.length <= 2) return p;
  return '…/' + parts.slice(-2).join('/');
}

export default function RepoSlugMenu({
  open,
  onClose,
  sessionId,
  currentPath,
  rebind,
  anchorClassName = 'absolute bottom-full left-0 z-50 mb-2',
}) {
  const [recents, setRecents] = useState(readRepoRecents);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // (Pedro 2026-06-25, sprint cursor-strip-4 R4-1) — repos do GitHub conectado.
  const [ghRepos, setGhRepos] = useState([]);
  const [ghLoading, setGhLoading] = useState(false);
  const [ghError, setGhError] = useState(null);
  const [repoFilter, setRepoFilter] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    function onChange(e) {
      if (Array.isArray(e?.detail?.paths)) setRecents(e.detail.paths);
    }
    function onStorage(e) {
      if (e.key === LS_RECENTS) setRecents(readRepoRecents());
    }
    window.addEventListener('apolo:repo:recents:change', onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('apolo:repo:recents:change', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Click-outside + Esc to close.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // (R4-1) — busca os repos do GitHub conectado ao abrir. apiFetch devolve
  // envelope { ok, status, data } — não Response. 404 GITHUB_NOT_CONNECTED é
  // estado honesto (sem token), não erro de UI.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setGhLoading(true);
    setGhError(null);
    apiFetch('/api/github/repos')
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 404) setGhError(null); // não conectado: silencioso
          else setGhError(res.error || `Falha HTTP ${res.status}`);
          setGhRepos([]);
          return;
        }
        setGhRepos(Array.isArray(res.data?.repos) ? res.data.repos : []);
      })
      .catch((e) => {
        if (!cancelled) setGhError(e?.message || 'Falha ao listar repos.');
      })
      .finally(() => {
        if (!cancelled) setGhLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const visibleRecents = useMemo(
    () => recents.filter((p) => p && p !== currentPath),
    [recents, currentPath],
  );

  const filteredGhRepos = useMemo(() => {
    const q = repoFilter.trim().toLowerCase();
    if (!q) return ghRepos;
    return ghRepos.filter(
      (r) => r.full_name?.toLowerCase().includes(q) || r.name?.toLowerCase().includes(q),
    );
  }, [ghRepos, repoFilter]);

  const handlePick = useCallback(async () => {
    setBusy(true);
    setError(null);
    const res = await openFolderForSession(sessionId, { rebind });
    setBusy(false);
    if (!res?.ok) {
      setError(res?.error || 'Falha ao abrir pasta.');
      return;
    }
    pushRepoRecent(res.path);
    onClose?.();
  }, [sessionId, rebind, onClose]);

  const handleRecent = useCallback(
    async (path) => {
      if (!path) return;
      // Re-bind to a known path WITHOUT opening the native picker. We synthesize
      // a `pick` shim that returns the chosen path so openFolderForSession's
      // rebind logic stays the single source of truth.
      const pickShim = async () => ({ ok: true, path, isGit: true });
      setBusy(true);
      setError(null);
      const res = await openFolderForSession(sessionId, { rebind, pick: pickShim });
      setBusy(false);
      if (!res?.ok) {
        setError(res?.error || 'Falha ao trocar de pasta.');
        return;
      }
      pushRepoRecent(res.path);
      onClose?.();
    },
    [sessionId, rebind, onClose],
  );

  // (R4-1) — abrir um repo do GitHub: resolve pra um checkout local
  // (reusa o existente ou clona determinístico em ~/.apolo/repos) e rebind a
  // sessão pro path resolvido. IPC real apolo:repo:resolve-remote.
  const handleGithubRepo = useCallback(
    async (r) => {
      if (!r?.full_name) return;
      if (!hasElectronBridge() || !window.apolo?.repo?.resolveRemote) {
        setError('Abrir repo do GitHub só funciona no app Electron.');
        return;
      }
      setBusy(true);
      setError(null);
      try {
        const resolved = await window.apolo.repo.resolveRemote({
          fullName: r.full_name,
          cloneUrl: r.clone_url,
          defaultBranch: r.default_branch || 'main',
        });
        if (!resolved?.ok || !resolved.path) {
          setError(resolved?.error || 'Falha ao resolver o repo do GitHub.');
          return;
        }
        const pickShim = async () => ({ ok: true, path: resolved.path, isGit: true });
        const res = await openFolderForSession(sessionId, { rebind, pick: pickShim });
        if (!res?.ok) {
          setError(res?.error || 'Falha ao vincular o repo.');
          return;
        }
        pushRepoRecent(res.path);
        onClose?.();
      } finally {
        setBusy(false);
      }
    },
    [sessionId, rebind, onClose],
  );

  if (!open) return null;
  const electron = hasElectronBridge();

  return (
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Trocar de repositório"
      data-testid="repo-slug-menu"
      className={
        anchorClassName +
        ' flex max-h-[420px] min-w-[280px] max-w-[360px] flex-col rounded-md border border-apolo-borderSubtle bg-apolo-surface1 p-1 shadow-xl backdrop-blur-md'
      }
    >
      <div className="flex items-center justify-between px-2 py-1.5">
        <span className="text-[10.5px] uppercase tracking-wide text-apolo-textTertiary">
          Trocar de pasta
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="inline-flex h-4 w-4 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          <X size={11} aria-hidden="true" />
        </button>
      </div>

      <button
        type="button"
        onClick={handlePick}
        disabled={!electron || busy}
        data-testid="repo-slug-pick"
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textPrimary transition hover:bg-apolo-surface2 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <FolderOpen size={13} aria-hidden="true" className="text-apolo-textSecondary" />
        <span>Abrir outra pasta…</span>
      </button>

      {visibleRecents.length > 0 && (
        <>
          <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
          <div className="px-2 py-1 text-[10.5px] uppercase tracking-wide text-apolo-textTertiary">
            <History size={10} aria-hidden="true" className="mr-1 inline-block" />
            Recentes
          </div>
          <ul className="flex flex-col">
            {visibleRecents.map((path) => (
              <li key={path}>
                <button
                  type="button"
                  onClick={() => handleRecent(path)}
                  disabled={!electron || busy}
                  data-testid="repo-slug-recent"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <span className="truncate font-mono" title={path}>{shortenPath(path)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* (Pedro 2026-06-25, sprint cursor-strip-4 R4-1) — TODOS os repos do
         GitHub conectado. Click resolve pra checkout local (reusa/clona) e
         rebind a sessão. Filtro porque a conta pode ter centenas. */}
      {(ghLoading || ghRepos.length > 0 || ghError) && (
        <>
          <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
          <div className="flex items-center gap-1.5 px-2 py-1 text-[10.5px] uppercase tracking-wide text-apolo-textTertiary">
            <Github size={10} aria-hidden="true" />
            Repositórios GitHub
            {ghLoading && <Loader2 size={10} aria-hidden="true" className="animate-spin" />}
          </div>
          {ghRepos.length > 8 && (
            <div className="mx-1 mb-1 flex items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1">
              <Search size={11} aria-hidden="true" className="text-apolo-textTertiary" />
              <input
                type="text"
                value={repoFilter}
                onChange={(e) => setRepoFilter(e.target.value)}
                placeholder="Buscar repositório…"
                aria-label="Filtrar repositórios GitHub"
                data-testid="repo-slug-gh-filter"
                className="block flex-1 bg-transparent text-[12px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
              />
            </div>
          )}
          {ghError && (
            <p role="alert" className="px-2 py-1 text-[10.5px] text-apolo-error">{ghError}</p>
          )}
          <ul className="flex max-h-[220px] flex-col overflow-y-auto">
            {filteredGhRepos.map((r) => (
              <li key={r.id || r.full_name}>
                <button
                  type="button"
                  onClick={() => handleGithubRepo(r)}
                  disabled={!electron || busy}
                  data-testid="repo-slug-gh-repo"
                  title={`Abrir ${r.full_name}${r.private ? ' (privado)' : ''}`}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {r.private ? (
                    <Lock size={11} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
                  ) : (
                    <Github size={11} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
                  )}
                  <span className="min-w-0 flex-1 truncate font-mono">{r.full_name}</span>
                  {r.language && (
                    <span className="shrink-0 text-[10px] text-apolo-textTertiary">{r.language}</span>
                  )}
                </button>
              </li>
            ))}
            {!ghLoading && ghRepos.length > 0 && filteredGhRepos.length === 0 && (
              <li className="px-2 py-1.5 text-[11px] text-apolo-textTertiary">
                Nenhum repo casou com “{repoFilter}”.
              </li>
            )}
          </ul>
        </>
      )}

      {!electron && (
        <p className="px-2 py-1.5 text-[10.5px] text-apolo-textTertiary">
          Trocar de pasta só funciona no app Electron.
        </p>
      )}
      {error && (
        <p role="alert" className="px-2 py-1.5 text-[10.5px] text-apolo-error">{error}</p>
      )}
      {!busy && currentPath && (
        <p className="border-t border-apolo-borderSubtle px-2 py-1.5 text-[10.5px] text-apolo-textTertiary">
          <Check size={10} aria-hidden="true" className="mr-1 inline-block" />
          Atual: <span className="font-mono">{shortenPath(currentPath)}</span>
        </p>
      )}
    </div>
  );
}
